import { DataType } from '@wix/editor-platform-ooi-metadata-resolver';
import type { ISettingsParam } from '@wix/tpa-settings';
import {
  createSettingsParam,
  createSettingsParams,
  SettingsParamType,
} from '@wix/tpa-settings';

import { MEMBER_NAME_HTML_TAG } from '../../constants/settings';

export type ISettingsParams = {
  showCoverPhoto: SettingsParamType.Boolean;
  showCoverPhotoDesktop: SettingsParamType.Boolean;
  showFollowButton: SettingsParamType.Boolean;
  showRoleLabel: SettingsParamType.Boolean;
  followButtonText: SettingsParamType.String;
  followingButtonText: SettingsParamType.String;
  messageButtonText: SettingsParamType.String;
  followingCountersText: SettingsParamType.String;
  followersCountersText: SettingsParamType.String;
  memberNameResponsiveHtmlTag: SettingsParamType.String;
  memberNameNonResponsiveHtmlTag: SettingsParamType.String;
  memberNameMobileHtmlTag: SettingsParamType.String;
};

export enum SettingsParamsTextKeys {
  followButtonText = 'followButtonText',
  followingButtonText = 'followingButtonText',
  messageButtonText = 'messageButtonText',
  followingCountersText = 'followingCountersText',
  followersCountersText = 'followersCountersText',
}

const showCoverPhoto = createSettingsParam('showCoverPhoto', {
  type: SettingsParamType.Boolean,
  getDefaultValue: () => true,
});

const textSettingsParam = (
  defaultValueKey: string,
): Omit<ISettingsParam<SettingsParamType.Text>, 'key'> => ({
  type: SettingsParamType.Text,
  getDefaultValue: ({ t }) => t(defaultValueKey),
  metadata: ({ t }) => ({
    type: DataType.text,
    defaultValue: {
      type: 'static',
      value: t(defaultValueKey),
    },
  }),
});

export default createSettingsParams<ISettingsParams>({
  showCoverPhoto,
  showCoverPhotoDesktop: {
    type: SettingsParamType.Boolean,
    key: 'pw-show-cover-photo-desktop',
    dangerousKeyTransformationOverride: () => 'pw-show-cover-photo-desktop',
    getDefaultValue: ({ getSettingParamValue }) => {
      const showCoverPhotoFallback = getSettingParamValue(showCoverPhoto);
      return showCoverPhotoFallback === undefined
        ? true
        : showCoverPhotoFallback;
    },
  },
  showFollowButton: {
    type: SettingsParamType.Boolean,
    getDefaultValue: () => true,
  },
  showRoleLabel: {
    type: SettingsParamType.Boolean,
    getDefaultValue: () => true,
  },
  followButtonText: textSettingsParam('profile-widget.follow'),
  followingButtonText: textSettingsParam('profile-widget.unfollow'),
  messageButtonText: textSettingsParam('profile-widget.message'),
  followingCountersText: textSettingsParam('profile-widget.following'),
  followersCountersText: textSettingsParam('profile-widget.followers'),
  memberNameResponsiveHtmlTag: {
    type: SettingsParamType.String,
    getDefaultValue: () => MEMBER_NAME_HTML_TAG.RESPONSIVE,
    metadata: {
      type: DataType.textEnum,
      defaultValue: {
        type: 'static',
        value: MEMBER_NAME_HTML_TAG.RESPONSIVE,
      },
    },
  },
  memberNameNonResponsiveHtmlTag: {
    type: SettingsParamType.String,
    getDefaultValue: () => MEMBER_NAME_HTML_TAG.NON_RESPONSIVE,
    metadata: {
      type: DataType.textEnum,
      defaultValue: {
        type: 'static',
        value: MEMBER_NAME_HTML_TAG.NON_RESPONSIVE,
      },
    },
  },
  memberNameMobileHtmlTag: {
    type: SettingsParamType.String,
    getDefaultValue: () => MEMBER_NAME_HTML_TAG.MOBILE,
    metadata: {
      type: DataType.textEnum,
      defaultValue: {
        type: 'static',
        value: MEMBER_NAME_HTML_TAG.MOBILE,
      },
    },
  },
});
